import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pull-to-refresh gesture for a scrollable element.
 *
 * The phone build has no refresh control anywhere — the tab bar switches views and the
 * lists load once on mount. A customer looking at a stale invoice list has no way to ask
 * for a new one short of killing the app, which in an installed PWA is not obvious.
 *
 * Returns the props to spread onto the scroll container plus the state needed to draw an
 * indicator. Kept separate from the component so the gesture can be tested without
 * rendering, and reused if a screen ever needs its own indicator.
 *
 * ── Three things here are load bearing ──────────────────────────────────────────
 *
 * 1. THE LISTENER IS ATTACHED NATIVELY, NOT VIA onTouchMove.
 *    React attaches `touchmove` as a PASSIVE listener, and `preventDefault()` on a
 *    passive listener does nothing — silently. The pull would appear to work in a
 *    desktop browser's device emulator and then fight the native overscroll on a real
 *    phone, because the browser scrolls anyway.
 *
 * 2. THE GESTURE ONLY ARMS AT scrollTop === 0.
 *    Checked on touchSTART, not on move. Checking on move means a fast upward flick that
 *    lands at the top mid-gesture suddenly arms the pull, and the list jumps.
 *
 * 3. RESISTANCE IS NOT DECORATION.
 *    A 1:1 pull feels broken because the content detaches from the finger. Damping to
 *    roughly a third matches the platform's own overscroll and, more practically, makes
 *    the threshold hard to cross by accident while scrolling.
 */

/** Distance the finger must travel before a release triggers a refresh. */
export const PULL_THRESHOLD = 70;

/** Cap on how far the content moves, however far the finger goes. */
export const MAX_PULL = 110;

/** Fraction of finger travel applied to the content. */
const RESISTANCE = 0.4;

/**
 * Below this, a downward drag is treated as an ordinary scroll attempt and left alone.
 * Without it, the smallest vertical jitter during a horizontal swipe steals the gesture.
 */
const ENGAGE_AFTER = 8;

export function usePullToRefresh(onRefresh, { disabled = false } = {}) {
    const containerRef = useRef(null);

    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    // Refs, not state: these are written on every touchmove and must not re-render.
    const startY = useRef(0);
    const armed = useRef(false);
    const engaged = useRef(false);

    // The latest onRefresh without re-binding listeners on every parent render.
    const onRefreshRef = useRef(onRefresh);
    useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

    const refreshingRef = useRef(false);
    useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);

    const runRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await onRefreshRef.current?.();
        } catch {
            // Deliberately swallowed, and `catch` rather than `finally` alone.
            //
            // `finally` clears the spinner but still RE-THROWS, so a failed refresh
            // produced an unhandled promise rejection on every pull — noisy in the
            // console and picked up as an unhandled error by anything watching.
            //
            // Swallowing is correct here because the error is already handled where it
            // belongs: `onRefresh` is the screen's own `load`, which dispatches a thunk,
            // and the slice's rejected case puts the server's message into state for
            // ListStates to render with a retry. Surfacing it twice would mean this
            // component inventing an error UI that duplicates that one.
        } finally {
            // Always clear. A spinner that never stops is a worse failure than the
            // stale list it was meant to fix, and a failing refresh is exactly when
            // it would otherwise happen.
            setRefreshing(false);
            setPullDistance(0);
        }
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || disabled) return undefined;

        const onTouchStart = (e) => {
            if (refreshingRef.current || e.touches.length !== 1) return;
            // Arm only when already at the top — see note 2.
            armed.current = el.scrollTop <= 0;
            engaged.current = false;
            startY.current = e.touches[0].clientY;
        };

        const onTouchMove = (e) => {
            if (!armed.current || refreshingRef.current) return;
            const delta = e.touches[0].clientY - startY.current;

            if (delta <= 0) {
                // Pulling up: hand the gesture back to normal scrolling.
                armed.current = false;
                engaged.current = false;
                setPullDistance(0);
                return;
            }
            if (!engaged.current && delta < ENGAGE_AFTER) return;
            engaged.current = true;

            // Non-passive listener, so this actually suppresses the native overscroll.
            if (e.cancelable) e.preventDefault();

            setPullDistance(Math.min(delta * RESISTANCE, MAX_PULL));
        };

        const onTouchEnd = () => {
            if (!armed.current) return;
            armed.current = false;
            const shouldRefresh = engaged.current && pullDistanceRef.current >= PULL_THRESHOLD;
            engaged.current = false;
            if (shouldRefresh) {
                runRefresh();
            } else {
                setPullDistance(0);
            }
        };

        // ⚠️ passive: false on touchmove — see note 1. The others may be passive.
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd, { passive: true });
        el.addEventListener('touchcancel', onTouchEnd, { passive: true });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [disabled, runRefresh]);

    // touchend reads the distance from a ref: the handler is bound once, so it would
    // otherwise close over the pullDistance from first render and never trigger.
    const pullDistanceRef = useRef(0);
    useEffect(() => { pullDistanceRef.current = pullDistance; }, [pullDistance]);

    return {
        containerRef,
        pullDistance,
        refreshing,
        /** True once the user has pulled far enough that releasing will refresh. */
        armedToRefresh: pullDistance >= PULL_THRESHOLD,
        /** Exposed for tests and for a screen that wants its own trigger. */
        refresh: runRefresh,
    };
}
